import { StartCallResponse } from './callsApi';
import { mungeOfferSdp } from './sdpMunge';

// Janus videoroom session for WebAdmin (OI-1564) — publisher (our camera/mic,
// munged for the iMX6 hub VPU) plus a subscriber for the remote peer's feed.
// Library-agnostic: it drives an injected adapter (the interfaces below) rather
// than importing janus.js directly, so the orchestration is unit-testable with a
// fake and the concrete adapter can be swapped/spiked. See plans/janus-call-migration.md.

/** Publisher must match the bitrate the Flutter/hub clients use (1.5 Mbps). */
export const PUBLISHER_BITRATE_BPS = 1_500_000;

export interface JanusJsep {
  type: string;
  sdp: string;
}

/** The subset of a janus.js videoroom plugin handle this session calls. */
export interface JanusVideoRoomHandle {
  send(opts: { message: Record<string, unknown>; jsep?: JanusJsep }): void;
  createOffer(opts: {
    media: { audio: boolean; video: boolean };
    // janus.js invokes this synchronously to let us mutate the offer SDP.
    customizeSdp: (jsep: JanusJsep) => void;
    success: (jsep: JanusJsep) => void;
    error: (err: unknown) => void;
  }): void;
  createAnswer(opts: {
    jsep: JanusJsep;
    // Subscriber is receive-only.
    media: { audioSend: false; videoSend: false };
    success: (jsep: JanusJsep) => void;
    error: (err: unknown) => void;
  }): void;
  handleRemoteJsep(opts: { jsep: JanusJsep }): void;
  hangup(): void;
  detach(): void;
}

interface AttachCallbacks {
  onAttached: (handle: JanusVideoRoomHandle) => void;
  onMessage: (msg: Record<string, unknown>, jsep?: JanusJsep) => void;
  onError: (err: unknown) => void;
}

export interface JanusConnection {
  // Publisher handle — surfaces our captured local media.
  attachVideoRoom(
    callbacks: AttachCallbacks & {
      onLocalStream?: (stream: MediaStream) => void;
    },
  ): void;
  // Subscriber handle — surfaces a remote publisher's media.
  attachSubscriber(
    callbacks: AttachCallbacks & {
      onRemoteStream?: (stream: MediaStream) => void;
    },
  ): void;
  destroy(): void;
}

/** Adapter over janus.js — opens a session to `url` authed by `token`. */
export type JanusConnectionFactory = (opts: {
  url: string;
  token: string;
  onConnected: (conn: JanusConnection) => void;
  onError: (err: unknown) => void;
}) => void;

export interface JanusCallSessionOptions {
  call: StartCallResponse; // janus_url, room_id, token from REST
  selfCustomerId: number; // display string MUST be `customer:{id}` (webhook contract)
  connect: JanusConnectionFactory;
  onError?: (err: unknown) => void;
  onLocalStream?: (stream: MediaStream) => void;
  onRemoteStream?: (stream: MediaStream) => void;
  onOfferSdp?: (sdp: string) => void; // the munged offer we publish (diagnostics)
  onAnswerSdp?: (sdp: string) => void; // Janus' SDP answer (diagnostics)
}

interface RemotePublisher {
  id: number;
  display?: string;
}

export class JanusCallSession {
  private readonly opts: JanusCallSessionOptions;
  private connection: JanusConnection | null = null;
  private handle: JanusVideoRoomHandle | null = null;
  private subscriber: JanusVideoRoomHandle | null = null;
  private negotiated = false;
  private subscribedFeeds = new Set<number>();

  constructor(opts: JanusCallSessionOptions) {
    this.opts = opts;
  }

  /** Connect → attach videoroom → join as publisher. */
  start(): void {
    const { call, connect } = this.opts;
    connect({
      url: call.janus_url,
      token: call.token,
      onError: (err) => this.fail(err),
      onConnected: (conn) => {
        this.connection = conn;
        conn.attachVideoRoom({
          onError: (err) => this.fail(err),
          onLocalStream: (stream) => this.opts.onLocalStream?.(stream),
          onAttached: (handle) => {
            this.handle = handle;
            handle.send({
              message: {
                request: 'join',
                ptype: 'publisher',
                room: call.room_id,
                // Exact format the REST Janus webhook parses (`^customer:(\d+)`).
                display: `customer:${this.opts.selfCustomerId}`,
              },
            });
          },
          onMessage: (msg, jsep) => this.onPublisherMessage(msg, jsep),
        });
      },
    });
  }

  private onPublisherMessage(
    msg: Record<string, unknown>,
    jsep?: JanusJsep,
  ): void {
    const handle = this.handle;
    if (!handle) return;

    // Our publisher slot is in the room — make the offer (once).
    if (msg.videoroom === 'joined' && !this.negotiated) {
      this.negotiated = true;
      handle.createOffer({
        media: { audio: true, video: true },
        // Apply the iMX6-hub SDP munges before the offer is set/sent.
        customizeSdp: (offer) => {
          offer.sdp = mungeOfferSdp(offer.sdp);
          this.opts.onOfferSdp?.(offer.sdp);
        },
        success: (offer) => {
          handle.send({
            message: {
              request: 'configure',
              audio: true,
              video: true,
              bitrate: PUBLISHER_BITRATE_BPS,
            },
            jsep: offer,
          });
        },
        error: (err) => this.fail(err),
      });
    }

    // Existing publishers arrive in `joined`; new ones in `event`. Subscribe to
    // each remote feed (anything that isn't our own publisher slot).
    const publishers = msg.publishers;
    if (Array.isArray(publishers)) {
      for (const p of publishers as RemotePublisher[]) {
        if (typeof p?.id === 'number') this.subscribeToFeed(p.id);
      }
    }

    // Janus' SDP answer to our publish completes negotiation.
    if (jsep) {
      this.opts.onAnswerSdp?.(jsep.sdp);
      handle.handleRemoteJsep({ jsep });
    }
  }

  private subscribeToFeed(feedId: number): void {
    const conn = this.connection;
    if (!conn || this.subscribedFeeds.has(feedId)) return;
    this.subscribedFeeds.add(feedId);

    conn.attachSubscriber({
      onError: (err) => this.fail(err),
      onRemoteStream: (stream) => this.opts.onRemoteStream?.(stream),
      onAttached: (handle) => {
        this.subscriber = handle;
        handle.send({
          message: {
            request: 'join',
            ptype: 'subscriber',
            room: this.opts.call.room_id,
            streams: [{ feed: feedId }],
          },
        });
      },
      onMessage: (_msg, jsep) => {
        const sub = this.subscriber;
        if (!sub || !jsep) return;
        // Janus offers the remote feed; answer receive-only and start.
        sub.createAnswer({
          jsep,
          media: { audioSend: false, videoSend: false },
          success: (answer) =>
            sub.send({ message: { request: 'start' }, jsep: answer }),
          error: (err) => this.fail(err),
        });
      },
    });
  }

  end(): void {
    try {
      this.subscriber?.hangup();
      this.subscriber?.detach();
      this.handle?.hangup();
      this.handle?.detach();
    } finally {
      this.subscriber = null;
      this.handle = null;
      this.connection?.destroy();
      this.connection = null;
    }
  }

  private fail(err: unknown): void {
    this.opts.onError?.(err);
  }
}
